import { useUser } from '@clerk/clerk-react';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { Divider } from 'primereact/divider';
import { InputTextarea } from 'primereact/inputtextarea';
import { ScrollPanel } from 'primereact/scrollpanel';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { v4 as uuidv4 } from 'uuid';
import {
  DocCompareChunkFinderChunk,
  docCompareChunkFinderEndpointPOST,
} from '~/api/def/endpoints/docCompare/docCompareChunkFinderEndpoint';
import { docCompareWholeDocumentEndpointPOST } from '~/api/def/endpoints/docCompare/docCompareWholeDocument';
import { ChatMessage } from '~/components/ui/misc/ChatMessage/ChatMessage';

type ChatMessage = {
  name: string | 'Guest' | 'Banqora';
  text: string;
  createdAt: string;
  isSender: boolean;
  hasPageBreaks?: boolean;
  uuid: string;
};

export type DocumentChatAiProps = {
  isOpen: boolean;
  documentId: string;
  onClickCitation: (highlight: DocCompareChunkFinderChunk) => void;
};

export const formatAiResponse = (response: string) => {
  if (!response) return '';

  const responseStr = response;

  // Check if the response contains page references
  if (!responseStr.includes("{'Page")) {
    return <Markdown remarkPlugins={[remarkBreaks]}>{responseStr}</Markdown>;
  }

  // Split by closing brace and filter out empty strings
  return responseStr.split('}').map((section) => {
    // Extract page number and content using a more precise regex
    const match = section.match(/{'Page (\d+)':\s*([\s\S]*)/);
    if (!match) return '';

    const [, pageNum, content] = match;

    return (
      <>
        <b>Page {pageNum}</b>
        <Markdown
          remarkPlugins={[remarkBreaks]}
          components={{
            ul: (props) => <ul style={{ paddingLeft: '1rem' }} {...props} />,
          }}
        >
          {content}
        </Markdown>
      </>
    );
  });
};

export const DocumentChatAi = ({
  isOpen,
  documentId,
  onClickCitation,
}: DocumentChatAiProps) => {
  const scrollPanelRef = useRef<ScrollPanel | null>(null);
  const shouldScrollRef = useRef(false);
  const { user } = useUser();
  const [query, setQuery] = useState<string>('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [selectedHighlight, setSelectedHighlight] = useState<
    Record<string, number>
  >({});
  const [chunks, setChunks] = useState<
    Record<string, DocCompareChunkFinderChunk[]>
  >({});

  const findChunksMutation = useMutation({
    mutationFn: async (queryText: string) => {
      const res = await docCompareChunkFinderEndpointPOST({
        query_text: queryText,
        number_of_chunks: 2,
        document_id: documentId,
        use_hyde: true,
      });

      if (res?.success === 1) {
        return res.data;
      }

      return [];
    },
  });

  const processDocumentMutation = useMutation({
    mutationFn: async ({
      chunks,
      question,
    }: {
      chunks: DocCompareChunkFinderChunk[];
      question: string;
    }) => {
      const res = await docCompareWholeDocumentEndpointPOST({
        chunks,
        question,
      });
      return res;
    },
  });

  const sendQuery = useCallback(async () => {
    if (!query) return;

    const agentUuid = uuidv4();

    setChatMessages((prevMessages) => [
      ...prevMessages,
      {
        name: user?.fullName || 'Guest',
        text: query,
        createdAt: format(new Date(), 'HH:mm'),
        isSender: true,
        uuid: uuidv4(),
      },
      {
        name: 'Banqora',
        text: 'Searching....',
        createdAt: format(new Date(), 'HH:mm'),
        isSender: false,
        uuid: agentUuid,
      },
    ]);

    const chunksResponse = await findChunksMutation.mutateAsync(query);

    setChunks((prevChunks) => ({
      ...prevChunks,
      [agentUuid]: chunksResponse,
    }));

    if (Array.isArray(chunksResponse)) {
      const aiResponse = await processDocumentMutation.mutateAsync({
        chunks: chunksResponse,
        question: query,
      });

      setChatMessages((prevMessages) => [
        ...prevMessages.slice(0, -1), // Remove the "Searching...." message
        {
          name: 'Banqora',
          text: aiResponse?.data?.toString() ?? '',
          createdAt: format(new Date(), 'HH:mm'),
          isSender: false,
          hasPageBreaks: aiResponse?.data?.includes('Page'),
          uuid: agentUuid,
        },
      ]);
    }
  }, [query, findChunksMutation, processDocumentMutation, user?.fullName]);

  useEffect(() => {
    shouldScrollRef.current = true;
  }, [chatMessages]);

  useLayoutEffect(() => {
    if (shouldScrollRef.current) {
      const element = scrollPanelRef.current?.getElement();
      if (element) {
        const contentDiv = element.querySelector('[data-pc-section="content"]');
        if (contentDiv) {
          contentDiv.scrollTo({
            top: contentDiv.scrollHeight,
            behavior: 'smooth',
          });
          shouldScrollRef.current = false;
        }
      }
    }
  }, [chatMessages]);

  return (
    <div
      className="relative mt-6 mr-2"
      style={{
        width: isOpen ? '500px' : '0px',
        overflow: 'hidden',
        transition: 'width 0.3s',
      }}
    >
      <div className="flex justify-content-between flex-column h-full">
        <ScrollPanel
          ref={scrollPanelRef}
          style={{ height: 'calc(100vh - 19.5rem)' }}
        >
          {chatMessages.length === 0 ? (
            <div style={{ height: 'calc(100% - 50px)', overflowY: 'auto' }}>
              <h2 className="text-center mt-5"> Ask anything below</h2>
              <p className="color-gray font-bold text-center">
                Ai can make mistakes. <br /> Double check important info.
              </p>
              {processDocumentMutation.data?.success === 1 &&
                processDocumentMutation.data?.data}
            </div>
          ) : (
            <div className="flex flex-column gap-3">
              {chatMessages.map((msg, index) => (
                <div key={index}>
                  <ChatMessage
                    key={index}
                    name={msg.name}
                    createdAt={msg.createdAt}
                    isSender={msg.isSender}
                  >
                    {formatAiResponse(msg.text)}

                    {msg.hasPageBreaks && (
                      <div className="flex gap-2 mt-2">
                        {chunks[msg.uuid]?.map((chunk, index) => (
                          <Chip
                            key={chunk.id}
                            label={`Citation ${index + 1}`}
                            onClick={() => {
                              setSelectedHighlight({ [chunk.id]: index });
                              onClickCitation(chunk);
                            }}
                            pt={{
                              root: {
                                style: {
                                  cursor: 'pointer',
                                  border: '1px solid #0168D9',
                                  background:
                                    selectedHighlight[chunk.id] === index
                                      ? '#0168D9'
                                      : 'white',
                                  color:
                                    selectedHighlight[chunk.id] === index
                                      ? 'white'
                                      : '#0168D9',
                                  fontSize: '0.875rem',
                                  height: 'auto',
                                  minHeight: 'auto',
                                },
                              },
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </ChatMessage>
                </div>
              ))}
            </div>
          )}
        </ScrollPanel>

        <Divider />

        <div>
          <InputTextarea
            value={query}
            placeholder="Ask anything about the document"
            style={{ width: '100%' }}
            onChange={(e) => setQuery(e.target.value)}
            rows={5}
          />
          <Button
            label="Send"
            className="absolute"
            disabled={
              !query ||
              findChunksMutation.isPending ||
              processDocumentMutation.isPending
            }
            onClick={sendQuery}
            style={{ bottom: '20px', right: '10px' }}
          />
        </div>
      </div>
    </div>
  );
};
